import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { actions } from '../../widgets/Wizard/wizard.slice'
import CustomDatePicker from '../../widgets/CustomDatePicker/DatePicker'
import { getLocalDateFromUTC } from '../../widgets/CustomDateRangePicker/CustomDateRangePicker.utils'
import {
	selectWizardFormValues,
	selectIsFflNumberValid,
} from '../../widgets/Wizard/wizard.selectors'
import { object as YupObject, string as YupString } from 'yup'
import { Formik } from 'formik'
import classNames from 'classnames'
import InputMask from 'react-input-mask'
import {
	isValidEmail,
	isValidFFLNumber,
	isValidZipcode,
} from '../../helpers/regex.utils'
import { fetchFFLDetails, selectFFLDetails } from '../Account'
import Form from '@/components/forms/legacy-form-shim'
import { isEmpty } from 'lodash'
import { US_STATES_LIST } from '../Customers/customers.constants'
import BusyIndicator, { REGIONS } from '../../widgets/busyIndicator'
import moment from 'moment/moment'

const { setFormValues, setIsCurrentPageValid, setIsFflNumberValid } = actions
const FFL_NUMBER = 15
const emptyString = ''
const MINIMUM_STRING_LENGTH = 3
const MINIMUM_ADDRESS_STRING_LENGTH = 2
const PHONE_NUMBER_LENGTH = 10

const VarianceRequest = () => {
	const dispatch = useDispatch()
	const wizardForm = useSelector(selectWizardFormValues)

	const [shouldDisabledFFLNumber, setShouldDisabledFFLNumber] = useState(false)
	const [errorMessage, setErrorMessage] = useState(emptyString)
	const [previousFFLNumber, setPreviousFFLNumber] = useState('')
	const fflDetails = useSelector(selectFFLDetails)
	const [isFflDetailsValid, setIsFflDetailsValid] = useState(false)
	const isFflNumberValid = useSelector(selectIsFflNumberValid)

	const {
		varianceFirstName,
		varianceLastName,
		varianceFFLNumber,
		premisesStreet,
		premisesCity,
		premisesState,
		premisesZipCode,
		mailingStreet,
		mailingCity,
		mailingState,
		mailingZipCode,
		varianceBusinessPhone,
		varianceEmail,
		varianceExpirationDate,
		sameAddress = false,
		varianceTradeName,
		varianceLoaIssueDate,
		varianceLoaExpirationDate,
		varianceLicenseName,
	} = wizardForm

	useEffect(() => {
		if (
			varianceFirstName &&
			varianceFirstName.length >= MINIMUM_STRING_LENGTH &&
			varianceLastName &&
			varianceLastName.length >= MINIMUM_STRING_LENGTH &&
			varianceFFLNumber &&
			isValidFFLNumber(varianceFFLNumber) &&
			mailingStreet &&
			mailingStreet.length >= MINIMUM_ADDRESS_STRING_LENGTH &&
			mailingCity &&
			mailingCity.length >= MINIMUM_ADDRESS_STRING_LENGTH &&
			mailingState &&
			mailingState.length >= MINIMUM_ADDRESS_STRING_LENGTH &&
			mailingZipCode &&
			isValidZipcode(mailingZipCode) &&
			varianceExpirationDate &&
			isValidEmail(varianceEmail)
		)
			dispatch(setIsCurrentPageValid(true))
		else dispatch(setIsCurrentPageValid(false))
	}, [
		dispatch,
		varianceFirstName,
		varianceLastName,
		varianceFFLNumber,
		premisesStreet,
		premisesCity,
		premisesState,
		premisesZipCode,
		mailingStreet,
		mailingCity,
		mailingState,
		mailingZipCode,
		varianceEmail,
		varianceExpirationDate,
		varianceLoaIssueDate,
		varianceLoaExpirationDate,
	])

	const initialValues = {
		firstName: varianceFirstName || '',
		lastName: varianceLastName || '',
		fflNumber: varianceFFLNumber || '',
		premisesStreetName: premisesStreet || '',
		premisesCityName: premisesCity || '',
		premisesStateName: premisesState || '',
		premisesZipcode: premisesZipCode || '',
		mailingStreetName: mailingStreet || '',
		mailingCityName: mailingCity || '',
		mailingStateName: mailingState || '',
		mailingZipcode: mailingZipCode || '',
		businessPhone: varianceBusinessPhone || '',
		email: varianceEmail || '',
		expirationDate:
			typeof varianceExpirationDate === 'undefined' ||
			varianceExpirationDate === null
				? ''
				: varianceExpirationDate || '',
		tradeName: varianceTradeName || '',
		licenseName: varianceLicenseName || '',
		loaIssueDate:
			typeof varianceLoaIssueDate === 'undefined' ||
			varianceLoaIssueDate === null
				? ''
				: varianceLoaIssueDate || '',
		loaExpirationDate:
			typeof varianceLoaExpirationDate === 'undefined' ||
			varianceLoaExpirationDate === null
				? ''
				: varianceLoaExpirationDate || '',
	}

	const validationSchema = YupObject().shape({
		firstName: YupString()
			.required('First Name is required')
			.min(MINIMUM_STRING_LENGTH, 'First Name should be at least 3 characters'),
		lastName: YupString()
			.required('Last Name is required')
			.min(MINIMUM_STRING_LENGTH, 'Last Name should be at least 3 characters'),
		fflNumber: YupString()
			.required('FFL Number is required')
			.test(
				'format',
				'FFL Number must be of valid format. (Ex: 1-23-456-78-9D-12345)',
				(val) => val && isValidFFLNumber(val)
			),
		mailingStreetName: YupString()
			.required('Mailing Street is Required')
			.min(MINIMUM_ADDRESS_STRING_LENGTH, 'At least 2 Character Required'),
		mailingCityName: YupString()
			.required('Mailing City is Required')
			.min(MINIMUM_ADDRESS_STRING_LENGTH, 'At least 2 Character Required'),
		mailingStateName: YupString()
			.required('Mailing State is Required')
			.min(MINIMUM_ADDRESS_STRING_LENGTH, 'At least 2 Character Required'),
		mailingZipcode: YupString()
			.required('Mailing Zipcode is Required')
			.test(
				'len',
				'ZipCode should be of length 5',
				(val) => val && isValidZipcode(val)
			),
		email: YupString()
			.email('E-mail is Invalid')
			.required('E-mail is required'),
		expirationDate: YupString().required('Expiration Date is required'),
	})

	const handleDispatchSetFormValues = (fflDetails) => {
		const {
			premiseStreetAddress1,
			premiseCity,
			premiseState,
			premiseZipCode,
			mailingStreetAddress1,
			mailingCity,
			mailingState,
			mailingZipCode,
			loaIssueDate,
			loaExpirationDate,
			tradeName,
			licenseName,
		} = fflDetails
		dispatch(
			setFormValues({
				premisesStreet: premiseStreetAddress1 || emptyString,
				premisesCity: premiseCity || emptyString,
				premisesState: premiseState || emptyString,
				premisesZipCode: premiseZipCode || null,
				mailingStreet: mailingStreetAddress1 || emptyString,
				mailingCity: mailingCity || emptyString,
				mailingState: mailingState || emptyString,
				mailingZipCode: mailingZipCode || null,
				varianceLoaIssueDate: loaIssueDate
					? new Date(loaIssueDate).toISOString()
					: emptyString,
				varianceLoaExpirationDate: loaExpirationDate
					? new Date(loaExpirationDate).toISOString()
					: emptyString,
				varianceTradeName: tradeName || emptyString,
				varianceLicenseName: licenseName || emptyString,
			})
		)
	}

	useEffect(() => {
		if (!isEmpty(fflDetails) && isFflDetailsValid) {
			if (sameAddress) {
				dispatch(
					setFormValues({
						mailingStreet: fflDetails.premiseStreetAddress1,
						mailingCity: fflDetails.premiseCity,
						mailingState: fflDetails.premiseState,
						mailingZipCode: fflDetails.premiseZipCode,
					})
				)
			}
			setIsFflDetailsValid(false)
		}
	}, [dispatch, fflDetails, sameAddress, isFflDetailsValid])

	const handleSetFieldValues = (
		setFieldValue,
		premiseStreetAddress1,
		premiseCity,
		premiseState,
		premiseZipCode,
		sameAddress,
		mailingStreetAddress1,
		mailingCity,
		mailingState,
		mailingZipCode,
		businessPhoneNumber,
		tradeName,
		licenseName,
		loaIssueDateOfFfl,
		loaExpirationDateOfFfl
	) => {
		setFieldValue('premisesStreetName', premiseStreetAddress1 || emptyString)
		setFieldValue('premisesCityName', premiseCity || emptyString)
		setFieldValue('premisesStateName', premiseState || emptyString)
		setFieldValue('premisesZipcode', premiseZipCode || emptyString)
		setFieldValue('mailingStreetName', mailingStreetAddress1 || emptyString)
		setFieldValue('mailingCityName', mailingCity || emptyString)
		setFieldValue('mailingStateName', mailingState || emptyString)
		setFieldValue('mailingZipcode', mailingZipCode || emptyString)
		setFieldValue('businessPhone', businessPhoneNumber || emptyString)
		setFieldValue('tradeName', tradeName || emptyString)
		setFieldValue('licenseName', licenseName || emptyString)
		setFieldValue('loaIssueDate', loaIssueDateOfFfl || emptyString)
		setFieldValue('loaExpirationDate', loaExpirationDateOfFfl || emptyString)

		businessPhoneNumber &&
			dispatch(
				setFormValues({
					varianceBusinessPhone: businessPhoneNumber,
				})
			)

		if (
			(sameAddress && premiseZipCode === emptyString) ||
			mailingZipCode === emptyString
		)
			dispatch(
				setFormValues({
					mailingStreet: emptyString,
					mailingCity: emptyString,
					mailingState: emptyString,
					mailingZipCode: emptyString,
				})
			)

		if (sameAddress) {
			setFieldValue('mailingStreetName', premiseStreetAddress1 || emptyString)
			setFieldValue('mailingCityName', premiseCity || emptyString)
			setFieldValue('mailingStateName', premiseState || emptyString)
			setFieldValue('mailingZipcode', premiseZipCode || emptyString)
		}
	}

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema}>
			{({
				values,
				errors,
				touched,
				handleBlur,
				setFieldValue,
				setFieldTouched,
			}) => {
				const handleSameAddress = (street, city, state, zipCode, isChecked) => {
					dispatch(
						setFormValues({
							sameAddress: isChecked,
						})
					)
					if (isChecked) {
						setFieldValue('mailingStreetName', street)
						setFieldValue('mailingCityName', city)
						setFieldValue('mailingStateName', state)
						setFieldValue('mailingZipcode', zipCode)
						setFieldTouched('mailingStreetName', false, false)
						setFieldTouched('mailingCityName', false, false)
						setFieldTouched('mailingStateName', false, false)
						setFieldTouched('mailingZipcode', false, false)
						dispatch(
							setFormValues({
								mailingStreet: street,
								mailingCity: city,
								mailingState: state,
								mailingZipCode: zipCode,
							})
						)
					}
				}

				const handleSetFflNumber = (ev) => {
					const value = ev.target.value.replace(/[^a-zA-Z0-9]/g, '')
					setFieldValue('fflNumber', value)
					dispatch(
						setFormValues({
							varianceFFLNumber: value,
						})
					)
				}

				const handleFFLNumberChange = (value) => {
					if (value.length === FFL_NUMBER && previousFFLNumber !== value) {
						dispatch(
							setFormValues({
								sameAddress: false,
							})
						)
						dispatch(setIsFflNumberValid(false))
						const filters = {
							firstThree: value.slice(0, 3),
							lastFive: value.slice(10),
						}
						setShouldDisabledFFLNumber(true)
						dispatch(fetchFFLDetails({ filters })).then(({ payload }) => {
							if (typeof payload !== 'string' && !isEmpty(payload)) {
								const {
									premiseStreetAddress1,
									premiseCity,
									premiseState,
									premiseZipCode,
									mailingStreetAddress1,
									mailingCity,
									mailingState,
									mailingZipCode,
									businessPhoneNumber,
									tradeName,
									licenseName,
									loaIssueDate,
									loaExpirationDate,
								} = payload
								const currentDate = new Date()
								const loaIssueDateOfFfl = moment(currentDate).isBetween(
									loaIssueDate,
									loaExpirationDate
								)
									? loaIssueDate
									: ''
								const loaExpirationDateOfFfl = moment(currentDate).isBetween(
									loaIssueDate,
									loaExpirationDate
								)
									? loaExpirationDate
									: ''

								handleDispatchSetFormValues({
									...payload,
									loaIssueDate: loaIssueDateOfFfl,
									loaExpirationDate: loaExpirationDateOfFfl,
								})
								const formattedPhoneNumber =
									businessPhoneNumber && businessPhoneNumber.replace(/\D/g, '')
								setErrorMessage(emptyString)
								setIsFflDetailsValid(true)
								handleSetFieldValues(
									setFieldValue,
									premiseStreetAddress1,
									premiseCity,
									premiseState,
									premiseZipCode,
									false,
									mailingStreetAddress1,
									mailingCity,
									mailingState,
									mailingZipCode,
									(formattedPhoneNumber.length === PHONE_NUMBER_LENGTH &&
										formattedPhoneNumber) ||
										emptyString,
									tradeName,
									licenseName,
									loaIssueDateOfFfl,
									loaExpirationDateOfFfl
								)
							} else {
								const formattedPayload = payload && JSON.parse(payload)
								handleDispatchSetFormValues({})
								setErrorMessage(formattedPayload)
								handleSetFieldValues(setFieldValue)
							}
							dispatch(
								setIsFflNumberValid(
									typeof payload !== 'string' && !isEmpty(payload)
								)
							)
							setShouldDisabledFFLNumber(false)
						})
					}
					setPreviousFFLNumber(value)
				}

				const handleOnBlur = (fieldKey, ev, formKey) => {
					const value = ev.target.value.trim()
					const formValue = { [formKey]: value }
					handleBlur(ev)
					setFieldValue(fieldKey, value)
					dispatch(setFormValues(formValue))
				}

				return (
					<>
						{!isEmpty(errorMessage) && (
							<div className='error-message mb-10'>{errorMessage}</div>
						)}
						<h6 className='mb-10 text-center custom-font-size'>
							{`We'll need the FFL details to complete account setup. We have
							partnered with the compliance experts at Orchid Advisor to assist
							you with this process.`}
						</h6>
						<div className='space-y-4'>
						<div className='border-bottom mb-3 pb-3'>
							<div className='flex flex-wrap -mx-1'>
								<div className='field-width px-1 w-full'>
									<div
										className={
											touched.fflNumber && errors.fflNumber ? 'mb-0' : 'mb-1'
										}
									>
										<Form.Label className='f-14'>FFL Number</Form.Label>
										<div className='flex items-center gap-2'>
											<div className='flex-1 min-w-0'>
												<InputMask
													type='text'
													name='fflNumber'
													placeholder='FFL Number'
													maskChar={null}
													onChange={handleSetFflNumber}
													onBlur={handleBlur}
													value={values.fflNumber}
													autoComplete='fflNumber'
													mask='*-**-***-**-**-*****'
													className={classNames('form-control', {
														error: touched.fflNumber && errors.fflNumber,
													})}
													readOnly={shouldDisabledFFLNumber}
												/>
											</div>
											<Button
												className='h-[50px] shrink-0'
												variant='primary'
												onClick={() =>
													values.fflNumber &&
													!errors.fflNumber &&
													handleFFLNumberChange(values.fflNumber)
												}
											>
												<BusyIndicator region={REGIONS.FETCH_ACCOUNTS}>
													Look Up
												</BusyIndicator>
											</Button>
										</div>
									</div>
								</div>
								<div className='ml-15 mr-15'>
									{touched.fflNumber && errors.fflNumber && (
										<div className='error-message'>{errors.fflNumber}</div>
									)}
								</div>
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>License Name</Form.Label>
									<Form.Control
										type='text'
										name='licenseName'
										placeholder='License Name'
										disabled
										value={values.licenseName}
										autoComplete='licenseName'
										onChange={(ev) => {
											setFieldValue('licenseName', ev.target.value)
											dispatch(
												setFormValues({
													varianceLicenseName: ev.target.value,
												})
											)
										}}
										onBlur={(ev) => {
											handleOnBlur('licenseName', ev, 'varianceLicenseName')
										}}
									/>
								</Form.Group>
							</div>
							<div className='px-1 w-full md:w-1/2'>
								{(values.tradeName || !isFflNumberValid) && (
									<Form.Group classname={'form-group'}>
										<Form.Label className='f-14'>Trade Name</Form.Label>
										<Form.Control
											type='text'
											name='tradeName'
											placeholder='Trade Name'
											disabled
											value={values.tradeName}
											autoComplete='tradeName'
											onChange={(ev) => {
												setFieldValue('tradeName', ev.target.value)
												dispatch(
													setFormValues({
														varianceTradeName: ev.target.value,
													})
												)
											}}
											onBlur={(ev) => {
												handleOnBlur('tradeName', ev, 'varianceTradeName')
											}}
										/>
									</Form.Group>
								)}
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>First Name</Form.Label>
									<Form.Control
										type='text'
										name='firstName'
										placeholder='First Name'
										disabled={!isFflNumberValid}
										value={values.firstName}
										autoComplete='firstName'
										onChange={(ev) => {
											setFieldValue('firstName', ev.target.value)
											dispatch(
												setFormValues({
													varianceFirstName: ev.target.value,
												})
											)
										}}
										onBlur={(ev) => {
											handleOnBlur('firstName', ev, 'varianceFirstName')
										}}
										className={classNames({
											error:
												isFflNumberValid &&
												touched.firstName &&
												errors.firstName,
										})}
									/>
									{isFflNumberValid &&
										touched.firstName &&
										errors.firstName && (
											<div className='error-message'>{errors.firstName}</div>
										)}
								</Form.Group>
							</div>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>Last Name</Form.Label>
									<Form.Control
										type='text'
										name='lastName'
										placeholder='Last Name'
										disabled={!isFflNumberValid}
										value={values.lastName}
										autoComplete='lastName'
										onChange={(ev) => {
											setFieldValue('lastName', ev.target.value)
											dispatch(
												setFormValues({
													varianceLastName: ev.target.value,
												})
											)
										}}
										onBlur={(ev) => {
											handleOnBlur('lastName', ev, 'varianceLastName')
										}}
										className={classNames({
											error:
												isFflNumberValid && touched.lastName && errors.lastName,
										})}
									/>
									{isFflNumberValid && touched.lastName && errors.lastName && (
										<div className='error-message'>{errors.lastName}</div>
									)}
								</Form.Group>
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>
										Business Phone Number
									</Form.Label>
									<InputMask
										name='businessPhone'
										mask='(999) 999-9999'
										disableunderline='true'
										disabled={true}
										maskChar={null}
										placeholder='(555) 555-5555'
										value={values.businessPhone}
										autoComplete='businessPhone'
										onChange={(ev) => {
											setFieldValue(
												'businessPhone',
												ev.target.value.replace(/[^a-zA-Z0-9]/g, '')
											)
											dispatch(
												setFormValues({
													varianceBusinessPhone: ev.target.value.replace(
														/[^a-zA-Z0-9]/g,
														''
													),
												})
											)
										}}
										onBlur={handleBlur}
										className='form-control'
									/>
								</Form.Group>
							</div>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group controlId='form-group formBasicEmail'>
									<Form.Label className='f-14'>Email</Form.Label>
									<Form.Control
										name='email'
										type='text'
										placeholder='Email Address'
										disabled={!isFflNumberValid}
										value={values.email}
										autoComplete='emailAddress'
										onChange={(ev) => {
											setFieldValue('email', ev.target.value)
											dispatch(
												setFormValues({ varianceEmail: ev.target.value })
											)
										}}
										onBlur={(ev) => {
											handleOnBlur('email', ev, 'varianceEmail')
										}}
										className={classNames({
											error: isFflNumberValid && touched.email && errors.email,
										})}
									/>
									{isFflNumberValid && touched.email && errors.email && (
										<div className='error-message'>{errors.email}</div>
									)}
								</Form.Group>
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Label className='f-14'>LOA Issue Date</Form.Label>
								<CustomDatePicker
									setDate={(date) =>
										dispatch(
											setFormValues({
												varianceLoaIssueDate: new Date(date).toISOString(),
											})
										)
									}
									date={
										varianceLoaIssueDate &&
										getLocalDateFromUTC(varianceLoaIssueDate)
									}
									placeholder='MM/DD/YYYY'
									startDate={new Date()}
									name='loaIssueDate'
									handleBlur={handleBlur}
									isDisabled={true}
								/>
							</div>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Label className='f-14'>LOA Expiration Date</Form.Label>
								<CustomDatePicker
									setDate={(date) =>
										dispatch(
											setFormValues({
												varianceLoaExpirationDate: new Date(date).toISOString(),
											})
										)
									}
									date={
										varianceLoaExpirationDate &&
										getLocalDateFromUTC(varianceLoaExpirationDate)
									}
									placeholder='MM/DD/YYYY'
									startDate={new Date()}
									name='loaExpirationDate'
									handleBlur={handleBlur}
									isDisabled={true}
								/>
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Label className='f-14'>Expiration Date</Form.Label>
								<CustomDatePicker
									setDate={(date) =>
										dispatch(
											setFormValues({
												varianceExpirationDate: new Date(date).toISOString(),
											})
										)
									}
									date={
										varianceExpirationDate &&
										getLocalDateFromUTC(varianceExpirationDate)
									}
									placeholder='MM/DD/YYYY'
									startDate={new Date()}
									name='expirationDate'
									handleBlur={handleBlur}
									isDisabled={!isFflNumberValid}
									touched={touched}
									errors={errors}
									isFflNumberValid={isFflNumberValid}
								/>
							</div>
						</div>
						<h6 className='font-semi-bold'>Premises Address</h6>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>Street Name</Form.Label>
									<Form.Control
										type='text'
										name='premisesStreetName'
										placeholder='123 Street Name'
										disabled
										value={values.premisesStreetName}
										autoComplete='premisesStreetName'
									/>
								</Form.Group>
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>City Name</Form.Label>
									<Form.Control
										type='text'
										name='premisesCityName'
										placeholder='City'
										disabled
										value={values.premisesCityName}
										autoComplete='premisesCityName'
									/>
								</Form.Group>
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>State</Form.Label>
									<Form.Control
										as='select'
										name='premisesStateName'
										placeholder='State'
										disabled
										value={values.premisesStateName}
										autoComplete='premisesStateName'
										className={classNames(
											values.premisesStateName
												? 'custom-scrollbar'
												: 'text-muted custom-scrollbar'
										)}
									>
										<option className='text-muted' value=''>
											State
										</option>
										{US_STATES_LIST.map((each) => (
											<option key={each.value} value={each.value}>
												{each.text}
											</option>
										))}
									</Form.Control>
								</Form.Group>
							</div>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>ZipCode</Form.Label>
									{values.premisesZipcode.length === 10 ? (
										<Form.Control
											type='text'
											name='premisesZipcode'
											placeholder='Zip Code'
											disabled
											value={values.premisesZipcode}
											autoComplete='premisesZipcode'
										/>
									) : (
										<InputMask
											type='text'
											mask='99999'
											disableunderline='true'
											disabled
											maskChar={null}
											name='premisesZipcode'
											placeholder='Zip Code'
											autoComplete='premisesZipcode'
											value={values.premisesZipcode}
											className={classNames('form-control')}
										/>
									)}
								</Form.Group>
							</div>
						</div>
						<h6 className='font-semi-bold'>Mailing Address</h6>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full'>
								<Form.Group className='form-group mb-1'>
									<Form.Check
										onChange={(ev) =>
											handleSameAddress(
												values.premisesStreetName,
												values.premisesCityName,
												values.premisesStateName,
												values.premisesZipcode,
												ev.target.checked
											)
										}
										disabled={
											!(
												values.premisesStreetName ||
												values.premisesCityName ||
												values.premisesStateName ||
												values.premisesZipcode
											)
										}
										checked={sameAddress}
										type='checkbox'
										label='Same as Premises Address'
										id='same-as-premises-address'
									/>
								</Form.Group>
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>Street Name</Form.Label>
									<Form.Control
										type='text'
										name='mailingStreetName'
										placeholder='123 Street Name'
										disabled={!isFflNumberValid}
										value={values.mailingStreetName}
										autoComplete='mailingStreetName'
										onChange={(ev) => {
											setFieldValue('mailingStreetName', ev.target.value)
											dispatch(
												setFormValues({
													mailingStreet: ev.target.value,
												})
											)
										}}
										onBlur={(ev) => {
											handleOnBlur('mailingStreetName', ev, 'mailingStreet')
										}}
										className={classNames('form-control text-nowrap', {
											error:
												isFflNumberValid &&
												touched.mailingStreetName &&
												errors.mailingStreetName,
										})}
										readOnly={sameAddress && values.premisesStreetName}
									/>
									{isFflNumberValid &&
										errors.mailingStreetName &&
										touched.mailingStreetName && (
											<div className='error-message'>
												{errors.mailingStreetName}
											</div>
										)}
								</Form.Group>
							</div>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>City Name</Form.Label>
									<Form.Control
										type='text'
										name='mailingCityName'
										placeholder='City'
										disabled={!isFflNumberValid}
										value={values.mailingCityName}
										autoComplete='mailingCityName'
										onChange={(ev) => {
											setFieldValue('mailingCityName', ev.target.value)
											dispatch(
												setFormValues({
													mailingCity: ev.target.value,
												})
											)
										}}
										onBlur={(ev) => {
											handleOnBlur('mailingCityName', ev, 'mailingCity')
										}}
										className={classNames('form-control text-nowrap', {
											error:
												isFflNumberValid &&
												touched.mailingCityName &&
												errors.mailingCityName,
										})}
										readOnly={sameAddress && values.premisesCityName}
									/>

									{isFflNumberValid &&
										errors.mailingCityName &&
										touched.mailingCityName && (
											<div className='error-message'>
												{errors.mailingCityName}
											</div>
										)}
								</Form.Group>
							</div>
						</div>
						<div className='flex flex-wrap -mx-1'>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>State</Form.Label>
									<Form.Control
										as='select'
										name='mailingStateName'
										disabled={
											!isFflNumberValid ||
											(sameAddress && values.premisesStateName)
										}
										placeholder='State'
										value={values.mailingStateName}
										autoComplete='mailingStateName'
										onChange={(ev) => {
											setFieldValue('mailingStateName', ev.target.value)
											dispatch(
												setFormValues({
													mailingState: ev.target.value,
												})
											)
										}}
										onBlur={handleBlur}
										className={classNames(
											values.mailingStateName
												? 'form-control text-nowrap custom-scrollbar'
												: 'text-muted form-control text-nowrap custom-scrollbar',
											!(
												!isFflNumberValid ||
												(sameAddress && values.premisesStateName)
											)
												? 'pointer'
												: '',
											{
												error:
													isFflNumberValid &&
													touched.mailingStateName &&
													errors.mailingStateName,
											}
										)}
										readOnly={sameAddress && values.premisesStateName}
									>
										<option value=''>State</option>
										{US_STATES_LIST.map((each) => (
											<option key={each.value} value={each.value}>
												{each.text}
											</option>
										))}
									</Form.Control>
									{isFflNumberValid &&
										errors.mailingStateName &&
										touched.mailingStateName && (
											<div className='error-message'>
												{errors.mailingStateName}
											</div>
										)}
								</Form.Group>
							</div>
							<div className='px-1 w-full md:w-1/2'>
								<Form.Group classname={'form-group'}>
									<Form.Label className='f-14'>ZipCode</Form.Label>
									{values.mailingZipcode.length === 10 ? (
										<Form.Control
											type='text'
											name='mailingZipcode'
											placeholder='Zip Code'
											disabled={!isFflNumberValid}
											value={values.mailingZipcode}
											autoComplete='mailingZipcode'
											onChange={(ev) => {
												setFieldValue('mailingZipcode', ev.target.value)
												dispatch(
													setFormValues({
														mailingZipCode: ev.target.value,
													})
												)
											}}
											onBlur={(ev) => {
												handleOnBlur('mailingZipcode', ev, 'mailingZipCode')
											}}
											className={classNames('form-control text-nowrap', {
												error: touched.mailingZipcode && errors.mailingZipcode,
											})}
											readOnly={sameAddress && values.premisesZipcode}
										/>
									) : (
										<InputMask
											type='text'
											mask='99999'
											disableunderline='true'
											disabled={!isFflNumberValid}
											maskChar={null}
											name='mailingZipcode'
											placeholder='Zip Code'
											value={values.mailingZipcode}
											autoComplete='mailingZipcode'
											onChange={(ev) => {
												setFieldValue('mailingZipcode', ev.target.value)
												dispatch(
													setFormValues({
														mailingZipCode: ev.target.value,
													})
												)
											}}
											onBlur={handleBlur}
											className={classNames('form-control text-nowrap', {
												error:
													isFflNumberValid &&
													touched.mailingZipcode &&
													errors.mailingZipcode,
											})}
											readOnly={sameAddress && values.premisesZipcode}
										/>
									)}
									{isFflNumberValid &&
										errors.mailingZipcode &&
										touched.mailingZipcode && (
											<div className='error-message'>
												{errors.mailingZipcode}
											</div>
										)}
								</Form.Group>
							</div>
						</div>
						</div>
					</>
				)
			}}
		</Formik>
	)
}

export default VarianceRequest
